import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemedToaster } from "@/components/ThemedToaster";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ClientAuthProvider, useClientAuth } from "@/context/ClientAuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Home from "@/pages/Home";
import Booking from "@/pages/Booking";
import MobileScan from "@/pages/MobileScan";
import QuotePage from "@/pages/QuotePage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ClientLogin from "@/pages/ClientLogin";
import ClientDashboard from "@/pages/ClientDashboard";
import ReviewPage from "@/pages/ReviewPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-600 font-mono-tech text-sm">Chargement…</div>;
  }
  if (!user) return <Navigate to="/admin" replace />;
  return children;
}

function ClientRoute({ children }) {
  const { user, loading } = useClientAuth();
  if (loading || user === null) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-600 font-mono-tech text-sm">Chargement…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  const firstLoad = useRef(true);
  useEffect(() => {
    window.scrollTo(0, 0);
    // GA4: initial page_view is auto-sent by gtag; track subsequent SPA navigations.
    if (firstLoad.current) { firstLoad.current = false; return; }
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: pathname,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [pathname]);
  return null;
}

// Handles the Google OAuth return: {origin}/account#session_id=...
function AuthCallback() {
  const navigate = useNavigate();
  const { googleLogin } = useClientAuth();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sid = m ? decodeURIComponent(m[1]) : null;
    (async () => {
      try {
        if (!sid) throw new Error("no session");
        await googleLogin(sid);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/account", { replace: true });
      } catch (e) {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/login?error=google", { replace: true });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-500 font-mono-tech text-sm">Connexion…</div>;
}

function AppRoutes() {
  const location = useLocation();
  // Process the OAuth session_id BEFORE any protected route runs (prevents race conditions).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/booking" element={<ClientRoute><Booking /></ClientRoute>} />
      <Route path="/scan-mobile/:sessionId" element={<MobileScan />} />
      <Route path="/devis/:token" element={<QuotePage />} />
      <Route path="/avis/:token" element={<ReviewPage />} />
      <Route path="/login" element={<ClientLogin />} />
      <Route path="/account" element={<ClientRoute><ClientDashboard /></ClientRoute>} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <ClientAuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AppRoutes />
            </BrowserRouter>
            <ThemedToaster />
          </ClientAuthProvider>
        </AuthProvider>
      </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
